import { Link } from "react-router-dom";
import { ArrowUpRight, Megaphone, Check, X, Layers } from "lucide-react";
import { Reveal } from "@/components/Motion";
import { usePageMeta } from "@/lib/seo";

const PILLARS = [
  ["01", "The cover image",
   "The single frame that decides whether a model opens your call. Use a high-resolution editorial or campaign photograph that conveys the mood and styling of the project — a look from the collection, a previous campaign, a moodboard hero. No stock graphics, no flyers buried under promotional type, no blurry screenshots. Models read the image first and the words second.",
   "https://images.unsplash.com/photo-1613915617430-8ab0fd7c6baf?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NjV8MHwxfHNlYXJjaHwyfHxmYXNoaW9uJTIwc3R1ZGlvJTIwc2hvb3QlMjBiYWNrc3RhZ2UlMjBjYXN0aW5nJTIwbW9kZWwlMjBlZGl0b3JpYWwlMjBjYW1wYWlnbiUyMHBob3Rvc2hvb3R8ZW58MHx8fHwxNzkzNjc2NTMwfDA&ixlib=rb-4.1.0&q=85",
   "aspect-[3/4]",
   "Model in sharp tailored coat posing against a studio backdrop"],
  ["02", "Location & call details",
   "Say exactly where and when. City and venue type — 'New York — daylight studio in Chelsea' — the call date, and the fitting or shoot dates if they're fixed. Vague postings like 'downtown studio, sometime next month' get scrolled past by the very faces you want. Add the practical criteria too: categories (runway, editorial, e-commerce, lookbook), gender, and minimum height — Solis filters by these, so complete details put your call in front of the right books instead of everyone's.",
   "https://images.unsplash.com/photo-1598006839649-5588feb1bae0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NjV8MHwxfHNlYXJjaHwzfHxmYXNoaW9uJTIwc3R1ZGlvJTIwc2hvb3QlMjBiYWNrc3RhZ2UlMjBjYXN0aW5nJTIwbW9kZWwlMjBlZGl0b3JpYWwlMjBjYW1wYWlnbiUyMHBob3Rvc2hvb3R8ZW58MHx8fHwxNzkzNjc2NTMwfDA&ixlib=rb-4.1.0&q=85",
   "aspect-[16/10]",
   "Professional studio lighting rig and camera setup with model on backdrop"],
  ["03", "Compensation & usage",
   "State the number. '$2,500/day + usage' will always outdraw 'competitive' or 'TBD' — the strongest books ignore calls that hide the rate. If it's trade or TFP, say so plainly and say what the model receives: retouched selects, team credits, tear sheets. Then state the usage: how long, where, and which channels — 'one year, global web and social' is a sentence that builds instant trust. Asking for broad usage without pay ends the conversation before it starts.",
   "https://images.unsplash.com/photo-1771919358565-059d9348b50b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzR8MHwxfHNlYXJjaHwxfHxmYXNoaW9uJTIwY2FtcGFpZ24lMjBtb29kYm9hcmQlMjBjcmVhdGl2ZSUyMHN0dWRpbyUyMGVkaXRvcmlhbHxlbmZ8MHx8fHwxNzkzNjc2NTN3fDA&ixlib=rb-4.1.0&q=85",
   "aspect-[3/4]",
   "Creative directors reviewing campaign lookbook proofs beside a wardrobe rack"],
  ["04", "The creative brief",
   "Three to five sentences, written like a treatment: the concept, the aesthetic reference, the wardrobe direction, and what the model should prepare or bring. 'AW26 tailoring story shot against raw concrete; relaxed suiting, minimal grooming; bring nude and black basics' tells a model exactly how to walk in. On Solis you can format the brief with line breaks and emphasis — use them. A brief that reads like a moodboard gets applications from models who already understand the job.",
   "https://images.unsplash.com/photo-1554881070-74595ca2b74c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NjV8MHwxfHNlYXJjaHw0fHxmYXNoaW9uJTIwc2hvdyUyMHJ1bndheSUyMGJhY2tzdGFnZSUyMGxpZ2h0aW5nJTIwbW9kZWx8ZW58MHx8fHwxNzkzNjc2NTM2fDA&ixlib=rb-4.1.0&q=85",
   "aspect-[16/9]",
   "Models walking a runway lineup during a casting review"],
  ["05", "The application window",
   "Tell talent how the call runs: when applications close, when they'll hear if they're shortlisted, and what happens next — in-person callback, video walk, or direct booking. Models hold dates for the houses that communicate; silence costs you the face you wanted. On Solis every applicant lands in your dashboard with their book and comp card attached — shortlist, book or decline from one place, and each outcome emails the model automatically.",
   "https://images.unsplash.com/photo-1753094881961-1e56e8e17a68?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NjV8MHwxfHNlYXJjaHw0fHxmYXNoaW9uJTIwc3R1ZGlvJTIwc2hvb3QlMjBiYWNrc3RhZ2UlMjBjYXN0aW5nJTIwbW9kZWwlMjBlZGl0b3JpYWwlMjBjYW1wYWlnbiUyMHBob3Rvc2hvb3R8ZW58MHx8fHwxNzkzNjc2NTMwfDA&ixlib=rb-4.1.0&q=85",
   "aspect-[3/4]",
   "Finished editorial campaign frame of a stylized model under studio spotlight"],
];

const DOS = [
  "Lead with a real campaign or editorial image — the mood of the job in one frame",
  "State the pay rate and usage terms in the first lines of the brief",
  "Give an exact city, venue and call date — plus fitting dates if fixed",
  "Set categories, gender and minimum height so the right books find you",
  "Answer every applicant — shortlist, book or decline, from your dashboard",
];

const DONTS = [
  "'Competitive rates' or 'negotiable' with no number attached — top books scroll past",
  "Vague locations like 'downtown studio' or dates like 'sometime in March'",
  "Broad or perpetual usage rights without compensation",
  "Flyer graphics buried under logos and promotional type",
  "Asking models to pay anything, or to move the conversation off-platform",
];

const PLANS = [
  ["Starter", "$49", "3 casting posts monthly", false],
  ["Studio", "$149", "12 casting posts monthly", false],
  ["Atelier", "$349", "Unlimited casting posts", true],
];

export default function CastingGuide() {
  usePageMeta({
    title: "The Casting Standard — How to Post a Professional Casting Call | Solis Casting",
    description: "How houses post casting calls that top models answer — the cover image, location and call details, transparent pay and usage, the creative brief, and how posting plans work on Solis Casting.",
    url: `${window.location.origin}/casting-guide`,
  });
  return (
    <div className="px-6 sm:px-10 py-24" data-testid="casting-guide-page">
      <Reveal>
        <p className="overline-label mb-4">For brands &amp; houses</p>
        <h1 className="font-display font-light text-4xl sm:text-6xl tracking-tighter mb-6">
          The Casting <span className="italic text-accent">Standard</span>
        </h1>
        <p className="text-sm text-white/60 max-w-2xl mb-24 leading-relaxed">
          The strongest books decide in seconds. A professional casting call needs one striking image,
          exact location and dates, transparent pay and usage, and a brief that reads like a treatment.
          This is what our talent expects to see before they apply — in the order they look for it.
        </p>
      </Reveal>

      <section className="mb-32" data-testid="guide-pillars">
        <Reveal>
          <p className="overline-label mb-4 flex items-center gap-2"><Megaphone size={14} className="text-accent" /> The five pillars</p>
          <h2 className="font-display font-light text-3xl sm:text-5xl tracking-tight mb-16">
            Every call needs <span className="italic text-accent">these five</span>
          </h2>
        </Reveal>
        <div className="border-t border-white/10">
          {PILLARS.map(([n, title, body, img, aspect, alt], i) => (
            <Reveal key={n} delay={i * 0.06}>
              <div className="grid grid-cols-12 gap-6 sm:gap-10 py-12 border-b border-white/10 items-center" data-testid={`guide-pillar-${i}`}>
                <div className={`col-span-12 ${aspect.includes("16/") ? "sm:col-span-6" : "sm:col-span-4 lg:col-span-3"} ${i % 2 ? "sm:order-2" : ""}`}>
                  <div className={`group overflow-hidden ${aspect} border border-white/10 hover:border-accent/50 transition-colors duration-500`} data-testid={`guide-pillar-photo-${i}`}>
                    <img
                      src={img}
                      alt={alt}
                      loading="lazy"
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                  </div>
                  <p className="font-grotesk text-[9px] uppercase tracking-[0.2em] text-white/35 mt-3">Pillar {n} — {title}</p>
                </div>
                <div className={`col-span-12 ${aspect.includes("16/") ? "sm:col-span-6" : "sm:col-span-8 lg:col-span-9"}`}>
                  <span className="font-display italic text-2xl text-accent block mb-2">{n}</span>
                  <span className="font-display text-3xl sm:text-4xl block mb-4">{title}</span>
                  <span className="text-sm text-white/70 leading-relaxed max-w-xl block">{body}</span>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="mb-32 grid grid-cols-1 lg:grid-cols-2 gap-16" data-testid="guide-rules">
        <Reveal>
          <div>
            <p className="overline-label mb-8">Do this</p>
            <div className="flex flex-col gap-5">
              {DOS.map((r, i) => (
                <div key={i} className="flex items-start gap-4" data-testid={`guide-do-${i}`}>
                  <Check size={16} className="text-emerald-400 shrink-0 mt-0.5" />
                  <p className="text-sm text-white/80 leading-relaxed">{r}</p>
                </div>
              ))}
            </div>
          </div>
        </Reveal>
        <Reveal delay={0.1}>
          <div>
            <p className="overline-label mb-8">Not this</p>
            <div className="flex flex-col gap-5">
              {DONTS.map((r, i) => (
                <div key={i} className="flex items-start gap-4" data-testid={`guide-dont-${i}`}>
                  <X size={16} className="text-accent shrink-0 mt-0.5" />
                  <p className="text-sm text-white/60 leading-relaxed">{r}</p>
                </div>
              ))}
            </div>
          </div>
        </Reveal>
      </section>

      <section className="mb-32" data-testid="guide-plans">
        <Reveal>
          <p className="overline-label mb-4 flex items-center gap-2"><Layers size={14} className="text-accent" /> Posting on Solis</p>
          <h2 className="font-display font-light text-3xl sm:text-5xl tracking-tight mb-8">
            What posting <span className="italic text-accent">costs</span>
          </h2>
          <p className="text-sm text-white/60 max-w-2xl mb-16 leading-relaxed">
            Post by membership, or keep it simple and pay per call. New houses start with a 14-day free
            trial on Starter — card required, nothing charged until day fifteen.
          </p>
        </Reveal>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-white/10 border border-white/10" data-testid="guide-plan-cards">
          {PLANS.map(([name, price, posts, flagship]) => (
            <div key={name} className="bg-background p-8" data-testid={`guide-plan-${name.toLowerCase()}`}>
              <p className="font-display text-3xl mb-2">{name}</p>
              <p className="mb-1">
                <span className="font-display text-5xl">{price}</span>
                <span className="font-grotesk text-xs uppercase tracking-[0.15em] text-muted-foreground"> / month</span>
              </p>
              <p className="font-grotesk text-xs uppercase tracking-[0.15em] text-white/60">{posts}</p>
              {flagship && (
                <p className="mt-4 font-grotesk text-[10px] uppercase tracking-[0.15em] text-accent">Recasts always free — expired calls reopen at no charge</p>
              )}
            </div>
          ))}
        </div>
        <Reveal>
          <div className="mt-12 max-w-3xl border-l-2 border-accent pl-8" data-testid="guide-single-post">
            <p className="overline-label mb-4 !text-[10px]">No membership? No problem</p>
            <p className="font-display italic text-2xl sm:text-3xl leading-snug text-white/90">
              Publish any single casting for $19.99 — no subscription, edit it freely while it's live.
            </p>
            <p className="font-grotesk text-[10px] uppercase tracking-[0.2em] text-white/40 mt-4">
              Members who run out of monthly posts can add a single call the same way. Recasting an expired listing is $19.99 — always free with Atelier.
            </p>
          </div>
        </Reveal>
      </section>

      <Reveal>
        <div className="flex flex-wrap items-center gap-6" data-testid="guide-cta">
          <Link to="/auth?mode=register&role=employer" data-testid="guide-post-cta" className="btn-primary inline-flex items-center gap-2">
            Post your casting <ArrowUpRight size={16} />
          </Link>
          <Link to="/castings" className="btn-outline">See the standard in action</Link>
        </div>
      </Reveal>
    </div>
  );
}
